import axios, { type AxiosInstance } from 'axios'
import { createEffect } from 'effector'

import { env } from '@/shared/config'

import { createChatAPIEffect } from './effect'
import {
    HistoryResponseSchema,
    type LoginRequest,
    LoginResponseSchema,
    type MessageTranslateRequest,
    MessageTranslateResponseSchema,
} from './protocol'
import type {
    ChannelJoinRequest,
    ChannelLeaveRequest,
    ChannelSlowmodeRequest,
    ChatAPI,
    HistoryRequest,
    MessageDeleteRequest,
    MessagePublishRequest,
    MessageReactionRequest,
    MessageReportRequest,
    UserMuteRequest,
    UserUnmuteRequest,
} from './types'

export class ChatAPIRemote implements ChatAPI {
    private axios: AxiosInstance

    constructor() {
        this.axios = axios.create({
            baseURL: env.CHAT_API_URL,
        })
    }

    setToken = createEffect<string | null, void>((token) => {
        if (token) {
            this.axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
        } else {
            delete this.axios.defaults.headers.common['Authorization']
        }
    })

    login = createChatAPIEffect(
        LoginResponseSchema,
        async (request: LoginRequest) => await this.axios.post(`/login`, request)
    )

    userMute = createChatAPIEffect<UserMuteRequest>(async (request) => await this.axios.post(`/user/mute`, request))

    userUnmute = createChatAPIEffect<UserUnmuteRequest>(
        async (request) => await this.axios.post(`/user/unmute`, request)
    )

    messagePublish = createChatAPIEffect<MessagePublishRequest>(
        async (request) => await this.axios.post(`/message/publish`, request)
    )

    messageReport = createChatAPIEffect<MessageReportRequest>(
        async (request) => await this.axios.post(`/message/report`, request)
    )

    messageDelete = createChatAPIEffect<MessageDeleteRequest>(
        async (request) => await this.axios.post(`/message/delete`, request)
    )

    messageReactionAdd = createChatAPIEffect<MessageReactionRequest>(
        async (request) => await this.axios.post(`/message/reaction/add`, request)
    )

    messageReactionRemove = createChatAPIEffect<MessageReactionRequest>(
        async (request) => await this.axios.post(`/message/reaction/remove`, request)
    )

    messageTranslate = createChatAPIEffect(
        MessageTranslateResponseSchema,
        async (request: MessageTranslateRequest) => await this.axios.post(`/message/translate`, request)
    )

    channelJoin = createChatAPIEffect<ChannelJoinRequest>(
        async (request) => await this.axios.post(`/channel/join`, request)
    )

    channelLeave = createChatAPIEffect<ChannelLeaveRequest>(
        async (request) => await this.axios.post(`/channel/leave`, request)
    )

    channelHistory = createChatAPIEffect(
        HistoryResponseSchema,
        async (request: HistoryRequest) => await this.axios.post(`/channel/history`, request)
    )

    channelSlowmodeSet = createChatAPIEffect<ChannelSlowmodeRequest>(
        async (request) => await this.axios.post(`/channel/slowmode/set`, request)
    )
}
